import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClockIcon } from 'lucide-react';
const DAY_MS = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + DAY_MS;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + DAY_MS;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(DAY_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + DAY_MS;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(DAY_MS);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = DAY_MS - remaining;
  const progress = Math.min(90, 65 + elapsed / DAY_MS * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-ink border border-burgundy rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_rgba(26,26,26,0.18)]" data-id="element-40" data-mp="1kzafcx9qc31j">
      <div className="text-center" data-id="element-41" data-mp="2ihivic0rz6a2">
        <div className="flex items-center justify-center gap-2 text-gold-light font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-42" data-mp="2iilkgeejeync">
          <AlarmClockIcon size={20} className="animate-pulse" data-id="element-43" data-mp="2ndekp1uyvfnx" />
          <span data-id="element-44" data-mp="2nbtkowpvv4io">The launch price ends in:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-45" data-mp="2jahuaoz6qf9f">
          <TimeBox value={pad(totalHours)} label="hrs" data-id="element-46" data-mp="2ebwuurrrbzm1" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-47" data-mp="29dvb04mibw9i">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-48" data-mp="29f6iaef5vm5d" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-49" data-mp="24mwrkw682i21">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-50" data-mp="1znskcb2uyuec" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-cream-dark mb-8" data-id="element-51" data-mp="1zlzc4rclk248">
          After this, the price goes up and it will not come back down.
        </p>

        <div className="space-y-2" data-id="element-52" data-mp="1qmuh3v08uqwt">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-53" data-mp="1loappf2vsmx2">
            <span className="text-cream-dark" data-id="element-54" data-mp="16icxbxrirh0">
              Launch price spots remaining
            </span>
            <span className="text-gold-light" data-id="element-55" data-mp="64tmrmo251z8">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-black/50 rounded-full overflow-hidden border border-burgundy" data-id="element-56" data-mp="b4fz7tw2ae6z">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-burgundy-light rounded-full" data-id="element-57" data-mp="f5rg9b0vdr3k" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-cream-dark font-medium" data-id="element-58" data-mp="3nlb6002c0rdv">
            {Math.round(progress)}% of spots already claimed
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-cream-light border-2 border-gold rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-59" data-mp="2zw0bh0bkrt86">
      <div className="flex items-baseline justify-center gap-1" data-id="element-60" data-mp="2gsorr1iixe48">
        <span className="text-4xl md:text-5xl font-bold text-ink tabular-nums font-serif" data-id="element-61" data-mp="2bsso53dqnop7">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-burgundy" data-id="element-62" data-mp="2qhosapcochkj">
          {label}
        </span>
      </div>
    </div>;
}